// Study Hall composer (DESIGN.md 16.3): textarea, a counter from 800 characters, a reply chip, edit
// mode, and @mention autocomplete from the authors in the local buffer only (there is no member
// directory). Enter sends, Shift+Enter adds a line, Esc cancels a reply or edit, Up in an empty
// composer edits your last message. No Alt shortcuts: they collide with the shell and the OS.
import * as C from "./copy.js";
import { h, icon } from "./view.js";

export const COUNTER_FROM = 800;

// The @mention being typed right before the caret: {start, query}, or null.
export function mentionAt(text, caret) {
	const m = /(^|[^A-Za-z0-9_])@([A-Za-z0-9_.-]{0,24})$/.exec(text.slice(0, caret));
	return m ? { start: caret - m[2].length - 1, query: m[2] } : null;
}

// What to type after "@" for a handle: the server resolves mentions by a folded key that ignores
// spaces, case and accents, so "José Diaz" is reachable as @JoseDiaz. "" when nothing is left.
export function mentionToken(handle) {
	const t = String(handle).normalize("NFKD").replace(/\p{M}/gu, "").replace(/[^A-Za-z0-9_.-]/g, "").slice(0, 24);
	return t.length >= 2 ? t : "";
}

// Prefix matches first, then substring matches, both in recency order.
export function mentionMatches(handles, query, max = 6) {
	const q = query.toLowerCase();
	const pre = [], mid = [];
	for (const hd of handles) {
		const t = mentionToken(hd);
		if (!t) continue;
		const l = t.toLowerCase();
		if (l.startsWith(q)) pre.push(hd);
		else if (q && l.includes(q)) mid.push(hd);
	}
	return pre.concat(mid).slice(0, max);
}

export const counterText = (len, max) => (len >= COUNTER_FROM ? C.COUNTER(len, max) : "");
export const overLimit = (text, lim) => text.length > lim.maxLen || text.split("\n").length > lim.maxLines;
export const hasLink = (text) => /https?:\/\//i.test(text);

export function createComposer({ store, onSend, onEdit, onTyping, onUpEdit }) {
	const S = store.S;
	let reply = null, editing = null, waitT = 0, waitUntil = 0, matches = [], sel = 0, mention = null, blockedText = "", roomName = "", busy = false, checkIn = false;
	// The reason shown while a listed-word cooldown runs (see cooldown() below).
	let coolMsg = "", coolUntil = 0;

	const ta = h("textarea", { class: "rv-hall-input", rows: "1", maxlength: "2000", spellcheck: "true", enterkeyhint: "send", autocomplete: "off", "aria-autocomplete": "list", "aria-controls": "rv-hall-mentions", "data-no-obf": "" });
	const chip = h("div", { class: "rv-hall-chipbar", hidden: true });
	const hint = h("p", { class: "rv-hall-hint", role: "status" });
	const counter = h("span", { class: "rv-hall-count" });
	const sendLabel = h("span", { id: "rv-hall-send-label", text: C.SEND });
	const send = h("button", { type: "button", class: "rv-btn rv-btn-primary rv-hall-send", "aria-labelledby": "rv-hall-send-label", onclick: () => submit() }, icon("send"), sendLabel);
	const menu = h("ul", { class: "rv-hall-mentions", id: "rv-hall-mentions", role: "listbox", hidden: true });
	const el = h("div", { class: "rv-hall-composer" }, chip, h("div", { class: "rv-hall-inputrow" }, menu, ta, counter, send), hint);

	function placeholder() {
		ta.placeholder = blockedText || (editing ? C.EDITING : checkIn ? C.CHECKIN_PLACEHOLDER : reply ? C.PLACEHOLDER_REPLY(reply.h) : C.PLACEHOLDER(roomName));
		ta.setAttribute("aria-label", ta.placeholder);
	}
	function setChip() {
		chip.textContent = "";
		const label = editing ? C.EDITING : reply ? C.REPLYING_TO(reply.h) : "";
		chip.hidden = !label;
		if (label) chip.append(h("span", { text: label }), h("button", { type: "button", class: "rv-hall-icon-btn", "aria-label": C.CANCEL, title: C.CANCEL, onclick: () => { cancel(); ta.focus(); } }, icon("close")));
		placeholder();
	}
	function resize() {
		ta.style.height = "auto";
		ta.style.height = Math.min(ta.scrollHeight, 180) + "px";
	}
	function refresh() {
		const v = ta.value;
		counter.textContent = counterText(v.length, S.limits.maxLen);
		counter.dataset.over = v.length > S.limits.maxLen ? "1" : "";
		if (!hint.dataset.kind || hint.dataset.kind === "info") {
			const probe = S.me && S.me.probation && hasLink(v);
			hint.textContent = probe ? C.LINK_PROBATION : checkIn ? C.CHECKIN_HINT : "";
			hint.dataset.kind = probe || checkIn ? "info" : "";
		}
		resize();
	}
	function setError(msg) { hint.textContent = msg || ""; hint.dataset.kind = msg ? "error" : ""; }
	function coolDone() {
		if (!coolMsg) return;
		// Only when the notice is still showing: once the person edits, it has already gone.
		if (hint.dataset.kind === "error" && hint.textContent.startsWith(coolMsg)) setError(coolMsg + " " + C.WORD_READY);
		coolMsg = ""; coolUntil = 0;
	}

	function cancel() {
		if (editing) ta.value = "";
		editing = null; reply = null;
		setChip(); refresh(); closeMenu();
	}
	const coolLeft = () => Math.ceil((coolUntil - Date.now()) / 1000);
	function submit() {
		// Enter during a word cooldown says how long is left (the button alone is silent).
		if (waitT && coolMsg && coolLeft() > 0) { setError(coolMsg + " " + C.WORD_WAIT(coolLeft())); return; }
		if (waitT || busy || ta.disabled) return;
		const v = ta.value;
		if (!v.trim()) return;
		if (overLimit(v, S.limits)) { setError(C.ERR.too_long); return; }
		setError("");
		if (editing) onEdit(editing, v);
		else onSend(v, reply);
	}

	// --- mention autocomplete ---
	function closeMenu() { menu.hidden = true; menu.textContent = ""; matches = []; mention = null; ta.removeAttribute("aria-activedescendant"); }
	function openMenu() {
		mention = mentionAt(ta.value, ta.selectionStart);
		matches = mention ? mentionMatches(store.authors(S.focus), mention.query) : [];
		if (!matches.length) { closeMenu(); return; }
		sel = Math.min(sel, matches.length - 1);
		menu.textContent = "";
		matches.forEach((hd, i) => {
			menu.append(h("li", { id: "rv-hall-m-" + i, role: "option", class: "rv-hall-mopt", "aria-selected": i === sel ? "true" : "false", text: hd, onmousedown: (ev) => { ev.preventDefault(); accept(i); } }));
		});
		ta.setAttribute("aria-activedescendant", "rv-hall-m-" + sel);
		menu.hidden = false;
	}
	function accept(i) {
		if (!mention || !matches[i]) return;
		const tok = "@" + mentionToken(matches[i]) + " ";
		const end = ta.selectionStart;
		ta.value = ta.value.slice(0, mention.start) + tok + ta.value.slice(end);
		const at = mention.start + tok.length;
		ta.setSelectionRange(at, at);
		closeMenu();
		refresh();
	}

	ta.addEventListener("keydown", (e) => {
		if (!menu.hidden && matches.length) {
			if (e.key === "ArrowDown" || e.key === "ArrowUp") { e.preventDefault(); sel = (sel + (e.key === "ArrowDown" ? 1 : matches.length - 1)) % matches.length; openMenu(); return; }
			// Enter on a name typed out in full sends; otherwise it picks the suggestion.
			const done = e.key === "Enter" && mention && mentionToken(matches[sel]).toLowerCase() === mention.query.toLowerCase();
			if (done) closeMenu();
			else if (e.key === "Enter" || e.key === "Tab") { e.preventDefault(); accept(sel); return; }
			if (e.key === "Escape") { e.preventDefault(); closeMenu(); return; }
		}
		if (e.key === "Enter" && !e.shiftKey && !e.isComposing) { e.preventDefault(); submit(); return; }
		if (e.key === "Escape" && (reply || editing)) { e.preventDefault(); cancel(); return; }
		if (e.key === "ArrowUp" && !ta.value && !editing && onUpEdit()) e.preventDefault();
	});
	ta.addEventListener("input", () => {
		if (hint.dataset.kind === "error") setError("");
		refresh();
		sel = 0;
		openMenu();
		if (ta.value.trim() && !editing) onTyping();
	});
	ta.addEventListener("click", () => openMenu());
	ta.addEventListener("blur", () => setTimeout(closeMenu, 120));

	return {
		el,
		focus() { if (!ta.disabled) ta.focus(); },
		setRoom(name) { roomName = name; placeholder(); },
		// Quiet Focus during a block: one short check-in (the server enforces it; this only explains).
		setCheckIn(on) {
			if (checkIn === !!on) return;
			checkIn = !!on;
			if (checkIn && reply) { reply = null; setChip(); }
			if (!hint.dataset.kind || hint.dataset.kind === "info") { hint.textContent = checkIn ? C.CHECKIN_HINT : ""; hint.dataset.kind = checkIn ? "info" : ""; }
			placeholder();
		},
		// Read-only: disables the box and shows why in its placeholder (announcements, locks, timeouts).
		setBlocked(text) {
			blockedText = text || "";
			ta.disabled = !!blockedText;
			send.disabled = !!blockedText || !!waitT || busy;
			if (blockedText) { closeMenu(); if (!editing) { reply = null; setChip(); } }
			placeholder();
		},
		setReply(m) { editing = null; reply = { id: m.id, h: m.a.h }; setChip(); ta.focus(); },
		setEdit(m) {
			reply = null; editing = m;
			ta.value = m.b;
			setChip(); refresh();
			ta.focus();
			ta.setSelectionRange(ta.value.length, ta.value.length);
		},
		editingId: () => (editing ? editing.id : 0),
		// An edit in flight: Enter does nothing until the server answers.
		setBusy(on) { busy = !!on; send.disabled = busy || !!blockedText || !!waitT; },
		cancel,
		clear() { ta.value = ""; reply = null; editing = null; setChip(); refresh(); closeMenu(); },
		// A failed send comes back: its text (when the box is empty) and its reply target.
		restore(text, rp) {
			if (!ta.value) ta.value = text;
			if (rp && !editing) reply = rp;
			setChip(); refresh();
		},
		error: setError,
		// A countdown on the send button for rate_limited / slowmode; nothing is posted meanwhile.
		wait(ms) {
			waitUntil = Date.now() + Math.max(1000, Number(ms) || 1000);
			const tick = () => {
				const left = Math.ceil((waitUntil - Date.now()) / 1000);
				if (left <= 0) { clearInterval(waitT); waitT = 0; sendLabel.textContent = C.SEND; delete send.dataset.wait; send.disabled = !!blockedText || busy; coolDone(); return; }
				sendLabel.textContent = C.WAIT(left);
			};
			if (waitT) clearInterval(waitT);
			// A disabled button drops keyboard focus to the page, so focus goes back to the box.
			if (document.activeElement === send) ta.focus();
			send.disabled = true;
			// Phones hide the button's label; this shows it while there is a countdown in it.
			send.dataset.wait = "1";
			waitT = setInterval(tick, 250);
			tick();
		},
		// Another room with nothing to wait for: the button is ready again.
		clearWait() {
			if (!waitT) return;
			clearInterval(waitT);
			waitT = 0;
			sendLabel.textContent = C.SEND;
			delete send.dataset.wait;
			send.disabled = !!blockedText || busy;
			coolDone();
		},
		// A message refused for a listed word. The text stays in the box to reword; the hint (a
		// polite live region) reads the reason and the wait once, the button counts down, and when
		// the wait ends the hint says so. The caller starts the countdown itself (wait()).
		cooldown(ms, msg) {
			coolMsg = msg || C.WORD_BLOCKED;
			coolUntil = Date.now() + Math.max(1000, Number(ms) || 0);
			setError(coolMsg + " " + C.WORD_WAIT(coolLeft()));
		},
		refresh,
	};
}
